import React, { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";

import { FlexContainer } from "components/Flex/Flex.Styles";
import { updateAutopaySettings } from "../../../api/autopay";
import { Lead, leadAtom } from "../../../atoms/lead";
import { PaymentProfile } from "../../../types/PaymentProfile";
import { TextStyled } from "../../Text/Text.Styles";
import { FormField } from "../Input/Input.Styles";
import RadioInput, { OptionType } from "../RadioInput/RadioInput";
import Select from "../Select/Select";
import { ButtonPrimary } from "./../../Buttons/Button.Styled";
import { PaymentStyled } from "./Payment.Styles";

interface PaymentProps {
  options: (OptionType & { options?: OptionType[] })[];
  defaultValue?: any;
  data: Lead & {
    pr_payment_profiles?: PaymentProfile[];
  };
  //onChange: (e: any) => void;
  onSend: (option: String) => void;
  showSuccessIndicator?: boolean;
}

const PaymentSuccessIndicator: React.FC = () => (
  <FlexContainer alignItems="center" style={{ marginTop: "10px" }}>
    <img
      src="../images/CheckCircle.svg"
      alt="Success"
      style={{ width: "25px", marginRight: "4px" }}
    />
    <TextStyled fontSize="base">Payment entry successful</TextStyled>
  </FlexContainer>
);

const Payment = (props: PaymentProps): React.JSX.Element => {
  const [option, setOption] = useState("");
  const prLeadData: any = useRecoilValue(leadAtom);
  const [selectedPaymentProfile, setSelectedPaymentProfile] =
    useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  //first take reponse lead data after saving to PR
  //if it's empty - get it from personal info
  const phone = (prLeadData as Lead)?.phone_number
    ? (prLeadData as Lead).phone_number
    : props.data.phone_number;
  const email = (prLeadData as Lead)?.email_address
    ? (prLeadData as Lead).email_address
    : props.data.email_address;

  const paymentProfiles = (prLeadData as Lead)?.pr_payment_profiles || [];
  const getPaymentProfileOptions = () => {
    const options = paymentProfiles.map((profile: PaymentProfile) => {
      let label = "";
      if (profile.type !== "cc") {
        const lastFour = profile.ach_account_number
          ? profile.ach_account_number.slice(-4)
          : "****";
        label = profile.bank_name
          ? `${profile.bank_name} account ending in ${lastFour}`
          : `ACH account ending in ${lastFour}`;
      } else {
        const lastFour = profile.cc_last_four || "****";
        const cardType = profile.cc_card_type || "Card";
        label = `${cardType} ending in ${lastFour}`;
      }

      return {
        value: profile.payment_profile_id.toString(),
        label,
        disabled: false,
      };
    });

    options.unshift({
      value: "not_on_autopay",
      label: "Not on autopay",
      disabled: false,
    });
    return options;
  };

  // Determine initial autopay selection
  useEffect(() => {
    setSaveError(null);
    if (paymentProfiles.length > 0) {
      const hasAutopayEnabled = paymentProfiles.some(
        (profile: PaymentProfile) =>
          profile.ach_auto_pay === 1 || profile.cc_auto_pay === 1
      );

      if (hasAutopayEnabled) {
        const autopayProfile = paymentProfiles.find(
          (profile: PaymentProfile) =>
            profile.ach_auto_pay === 1 || profile.cc_auto_pay === 1
        );
        setSelectedPaymentProfile(
          autopayProfile?.payment_profile_id.toString() || ""
        );
      } else {
        setSelectedPaymentProfile("not_on_autopay");
      }
    }
  }, [paymentProfiles]);

  const changeOption = (e: any) => {
    setOption(e);
  };

  useEffect(() => {
    if (props.defaultValue) {
      changeOption(props.defaultValue);
    }
  }, [props.defaultValue]);

  const handleSaveAutopay = async () => {
    if (!prLeadData.pr_customer_id) {
      setSaveError("Customer ID not found");
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await updateAutopaySettings(
        prLeadData.pr_customer_id.toString(),
        selectedPaymentProfile
      );
      setSaveSuccess(true);
    } catch (error) {
      setSaveError("Failed to update autopay settings");
      console.error("Failed to update autopay settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PaymentStyled data-testid="payment">
      <RadioInput
        name="payment_option"
        options={props.options}
        onChange={changeOption}
        defaultValue={props.defaultValue}
      />
      {option && (
        <FormField className="info__panel">
          {option === "email" && (
            <div className={`payment__additional-info__${option}`}>
              <div>
                <TextStyled>Email address</TextStyled>
                <input
                  placeholder="e.g., john.doe@example.com"
                  name="email_address"
                  className="_large-field"
                  value={email}
                  readOnly
                />
              </div>
              <FlexContainer justifyContent="space-between" alignItems="center">
                <div>
                  {props.showSuccessIndicator && <PaymentSuccessIndicator />}
                </div>
                <ButtonPrimary
                  className="_p-right"
                  onClick={() => props.onSend("email")}
                >
                  Send
                </ButtonPrimary>
              </FlexContainer>
            </div>
          )}
          {option === "sms" && (
            <div className={`payment__additional-info__${option}`}>
              <div>
                <TextStyled>Phone number</TextStyled>
                <input
                  placeholder="e.g., 555-867-5309"
                  name="phone_number"
                  className="_large-field"
                  value={phone}
                  readOnly
                />
              </div>
              <FlexContainer justifyContent="space-between" alignItems="center">
                <div>
                  {props.showSuccessIndicator && <PaymentSuccessIndicator />}
                </div>
                <ButtonPrimary
                  className="_p-right"
                  onClick={() => props.onSend("sms")}
                >
                  Send
                </ButtonPrimary>
              </FlexContainer>
            </div>
          )}
        </FormField>
      )}{" "}
      {paymentProfiles.length > 0 && (
        <FormField className="autopay__panel" style={{ marginTop: "10px" }}>
          <TextStyled style={{ marginBottom: "8px" }}>
            Autopay payment method
          </TextStyled>
          <div>
            <FlexContainer justifyContent="space-between" alignItems="center">
              <div style={{ flex: 1 }}>
                <Select
                  options={getPaymentProfileOptions()}
                  value={selectedPaymentProfile}
                  onChange={(value) => {
                    if (value || value === selectedPaymentProfile) {
                      setSelectedPaymentProfile(value);
                      setSaveError(null);
                      setSaveSuccess(false);
                    }
                  }}
                  placeholder="Select payment method"
                  disabled={isSaving}
                />
              </div>
              <ButtonPrimary
                style={{ marginLeft: "16px" }}
                onClick={handleSaveAutopay}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </ButtonPrimary>
            </FlexContainer>
            {saveError && (
              <div style={{ marginTop: "8px" }}>
                <TextStyled
                  style={{
                    color: "red",
                    fontSize: "14px",
                  }}
                >
                  {saveError}
                </TextStyled>
              </div>
            )}
            {saveSuccess && !saveError && (
              <div style={{ marginTop: "8px" }}>
                <TextStyled
                  style={{
                    color: "green",
                    fontSize: "14px",
                  }}
                >
                  Autopay preference saved successfully
                </TextStyled>
              </div>
            )}
          </div>
        </FormField>
      )}
    </PaymentStyled>
  );
};

export default Payment;
